import { useState } from 'react'
import { lookupWikidataWork } from '@/services/api/concerts'
import type { AddProgrammeItemInput } from '@/services/api/concerts'
import { Button } from '@brent/core-ui/ui'
import { Input } from '@brent/core-ui/ui'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@brent/core-ui/ui'
import { Loader2 } from 'lucide-react'

interface AddProgrammeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (input: AddProgrammeItemInput) => Promise<void>
  isPending: boolean
}

export function AddProgrammeDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
}: AddProgrammeDialogProps) {
  const [workTitle, setWorkTitle] = useState('')
  const [composerName, setComposerName] = useState('')
  const [workQid, setWorkQid] = useState('')
  const [composerQid, setComposerQid] = useState('')
  const [arrangementNote, setArrangementNote] = useState('')
  const [notes, setNotes] = useState('')
  const [lookupQid, setLookupQid] = useState('')
  const [isLookingUp, setIsLookingUp] = useState(false)
  const [lookupError, setLookupError] = useState('')

  const resetForm = () => {
    setWorkTitle('')
    setComposerName('')
    setWorkQid('')
    setComposerQid('')
    setArrangementNote('')
    setNotes('')
    setLookupQid('')
    setIsLookingUp(false)
    setLookupError('')
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) resetForm()
    onOpenChange(nextOpen)
  }

  const handleLookup = async () => {
    const qid = lookupQid.trim()
    if (!qid || !qid.startsWith('Q')) {
      setLookupError('QID must start with Q (e.g. Q7302)')
      return
    }

    setIsLookingUp(true)
    setLookupError('')

    try {
      const result = await lookupWikidataWork(qid)
      if (result.ok && result.title) {
        setWorkTitle(result.title)
        setWorkQid(qid)
        if (result.composer_name) setComposerName(result.composer_name)
        if (result.composer_qid) setComposerQid(result.composer_qid)
        setLookupError('')
      } else {
        setLookupError(result.error || `Could not find work ${qid} on Wikidata`)
      }
    } catch {
      setLookupError('Failed to look up work on Wikidata')
    } finally {
      setIsLookingUp(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!workTitle.trim()) return

    const input: AddProgrammeItemInput = {
      work_title: workTitle.trim(),
      composer_name: composerName.trim() || undefined,
      work_qid: workQid.trim() || undefined,
      composer_qid: composerQid.trim() || undefined,
      arrangement_note: arrangementNote.trim() || undefined,
      notes: notes.trim() || undefined,
    }

    await onSubmit(input)
    resetForm()
  }

  const isValid = workTitle.trim().length > 0

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Programme Item</DialogTitle>
          <DialogDescription>
            Look up a work on Wikidata by QID, or enter details manually.
          </DialogDescription>
        </DialogHeader>

        {/* Wikidata lookup section */}
        <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
          <label htmlFor="wikidata-qid-lookup" className="text-sm font-medium">
            Wikidata Lookup
          </label>
          <div className="flex gap-2">
            <Input
              id="wikidata-qid-lookup"
              value={lookupQid}
              onChange={(e) => setLookupQid(e.target.value)}
              placeholder="Work QID (e.g. Q7302)"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleLookup()
                }
              }}
            />
            <Button
              type="button"
              variant="secondary"
              onClick={handleLookup}
              disabled={isLookingUp || !lookupQid.trim()}
            >
              {isLookingUp ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Look up'}
            </Button>
          </div>
          {lookupError && <p className="text-sm text-destructive">{lookupError}</p>}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label htmlFor="prog-work-title" className="text-sm font-medium">
                Work title *
              </label>
              <Input
                id="prog-work-title"
                value={workTitle}
                onChange={(e) => setWorkTitle(e.target.value)}
                placeholder="e.g. Symphony No. 5"
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="prog-composer" className="text-sm font-medium">
                Composer
              </label>
              <Input
                id="prog-composer"
                value={composerName}
                onChange={(e) => setComposerName(e.target.value)}
                placeholder="e.g. Beethoven"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="prog-work-qid" className="text-sm font-medium">
                Work QID
              </label>
              <Input
                id="prog-work-qid"
                value={workQid}
                onChange={(e) => setWorkQid(e.target.value)}
                placeholder="e.g. Q7302"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="prog-composer-qid" className="text-sm font-medium">
                Composer QID
              </label>
              <Input
                id="prog-composer-qid"
                value={composerQid}
                onChange={(e) => setComposerQid(e.target.value)}
                placeholder="e.g. Q255"
              />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label htmlFor="prog-arrangement" className="text-sm font-medium">
                Arrangement note
              </label>
              <Input
                id="prog-arrangement"
                value={arrangementNote}
                onChange={(e) => setArrangementNote(e.target.value)}
                placeholder="e.g. arr. Stokowski"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="prog-notes" className="text-sm font-medium">
                Notes
              </label>
              <Input
                id="prog-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid || isPending}>
              {isPending ? 'Adding...' : 'Add to Programme'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
